import { useMemo } from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  FilterMultiSelect,
  FilterSelect,
} from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import { useIsFeatureEnabled } from '../../feature-enablement/featureEnablement';
import { implementedActions } from '../actions';

const ACTION_TYPE_OPTIONS = implementedActions;

export const rulesQueryParser = z.object({
  pageSize: z.coerce.number().optional().default(15),
  page: z.coerce.number().optional().default(1),
  name: z.coerce.string().optional(),
  queues: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  enabled: z.coerce.string().optional(),
  linked: z.coerce.string().optional(),
  action_type: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(ACTION_TYPE_OPTIONS).array())
    .optional()
    .catch(undefined),
});

const ENABLED_OPTIONS = ['true', 'false'] as const;
const LINKED_OPTIONS = ['true', 'false'] as const;

export type RulesQuery = Partial<z.infer<typeof rulesQueryParser>>;

type UseRulesFilterSettingsProps = {
  queues: Queue[] | undefined;
  workspaces: Workspace[] | undefined;
  isQueuesLoading: boolean;
  canChangeQueue?: boolean;
  setFilters: (state: RulesQuery) => void;
};

export const useRulesFilterSettings = ({
  queues,
  workspaces,
  isQueuesLoading,
  canChangeQueue = true,
  setFilters,
}: UseRulesFilterSettingsProps) => {
  const intl = useIntl();

  const isRuleManagerEnabled = useIsFeatureEnabled('rule-manager');

  const workspaceOptions = useMemo(() => {
    return workspaces
      ? workspaces
          .map(w => ({
            id: w.id.toString(),
            label: w.name,
            options: w.queues.map(q => getIDFromUrl(q).toString()),
          }))
          .filter(ws => ws.options.length > 0)
      : undefined;
  }, [workspaces]);

  const settings = useMemo(
    () => [
      ...(canChangeQueue
        ? [
            createSettings({
              filterKey: 'queues',
              name: intl.formatMessage({
                id: 'features.queues.queueSelect.label',
              }),
              options: workspaceOptions ?? [],
              optionsAreLoading: isQueuesLoading,
              getOptionLabel: option =>
                queues?.find(q => q.id === Number(option))?.name ?? '',
              getChipLabel: value =>
                value
                  .map(v => queues?.find(q => q.id === v)?.name ?? '')
                  .filter(Boolean)
                  .join(', '),
              valueGetter: (currentValue: RulesQuery) => currentValue.queues,
              validateFilter: value =>
                !value.every(v => queues?.map(q => q.id).includes(v)),
              setFilter: value => setFilters({ queues: value, page: 1 }),
              Component: FilterMultiSelect,
            }),
          ]
        : []),
      createSettings({
        filterKey: 'enabled',
        name: intl.formatMessage({
          id: 'features.rules.enabled.label',
        }),
        options: [...ENABLED_OPTIONS],
        getOptionLabel: option =>
          R.isIncludedIn(option, ENABLED_OPTIONS)
            ? intl.formatMessage({ id: `features.rules.enabled.${option}` })
            : option,
        getChipLabel: value =>
          R.isIncludedIn(value, ENABLED_OPTIONS)
            ? intl.formatMessage({ id: `features.rules.enabled.${value}` })
            : value,
        valueGetter: (currentValue: RulesQuery) => currentValue.enabled,
        validateFilter: value => !R.isIncludedIn(value, ENABLED_OPTIONS),
        setFilter: value => setFilters({ enabled: value, page: 1 }),
        Component: FilterSelect,
      }),
      ...(isRuleManagerEnabled
        ? [
            createSettings({
              filterKey: 'linked',
              name: intl.formatMessage({
                id: 'features.rules.linked.label',
              }),
              options: [...LINKED_OPTIONS],
              getOptionLabel: option =>
                R.isIncludedIn(option, LINKED_OPTIONS)
                  ? intl.formatMessage({
                      id: `features.rules.linked.${option}`,
                    })
                  : option,
              getChipLabel: value =>
                R.isIncludedIn(value, LINKED_OPTIONS)
                  ? intl.formatMessage({ id: `features.rules.linked.${value}` })
                  : value,
              valueGetter: (currentValue: RulesQuery) => currentValue.linked,
              validateFilter: value => !R.isIncludedIn(value, LINKED_OPTIONS),
              setFilter: value => setFilters({ linked: value, page: 1 }),
              Component: FilterSelect,
            }),
          ]
        : []),
      createSettings({
        filterKey: 'action_type',
        name: intl.formatMessage({
          id: 'features.rules.actionType.label',
        }),
        options: [...ACTION_TYPE_OPTIONS],
        getOptionLabel: option =>
          R.isIncludedIn(option, ACTION_TYPE_OPTIONS)
            ? intl.formatMessage({ id: `features.rules.actionType.${option}` })
            : option,
        getChipLabel: value =>
          value
            .filter((v): v is (typeof ACTION_TYPE_OPTIONS)[number] =>
              R.isIncludedIn(v, ACTION_TYPE_OPTIONS)
            )
            .map(v =>
              intl.formatMessage({ id: `features.rules.actionType.${v}` })
            )
            .join(', '),
        valueGetter: (currentValue: RulesQuery) => currentValue.action_type,
        validateFilter: value =>
          !value.every(v => R.isIncludedIn(v, ACTION_TYPE_OPTIONS)),
        setFilter: value => setFilters({ action_type: value, page: 1 }),
        Component: FilterMultiSelect,
      }),
    ],
    [
      canChangeQueue,
      intl,
      workspaceOptions,
      isQueuesLoading,
      queues,
      setFilters,
      isRuleManagerEnabled,
    ]
  );

  return settings;
};
